"use client";

import { ArrowRight, CalendarDays, CheckCircle2, Loader2 } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useState } from "react";
import type { FormEvent } from "react";
import {
  biggestPainOptions,
  companyTypeOptions,
  monthlyLoadVolumeOptions,
  productInterestOptions,
  timelineOptions,
  type BiggestPain,
  type CompanyType,
  type DemoTimeline,
  type MonthlyLoadVolume,
  type ProductInterest,
} from "@/core/marketing/demo-leads";

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "error"; message: string }
  | { status: "submitted"; calendarEligible: boolean; calendarUrl?: string };

const companyTypeLabels: Record<CompanyType, string> = {
  broker_3pl: "Broker / 3PL",
  shipper: "Shipper",
  freight_tech: "Freight tech / AI builder",
  carrier: "Carrier",
  other: "Other",
};

const monthlyLoadVolumeLabels: Record<MonthlyLoadVolume, string> = {
  under_25: "Under 25",
  "25_100": "25-100",
  "101_500": "101-500",
  "500_plus": "500+",
};

const biggestPainLabels: Record<BiggestPain, string> = {
  exceptions: "Exceptions",
  carrier_vetting: "Carrier vetting",
  tracking: "Tracking",
  documents: "Documents",
  ai_ops: "AI operations",
  api: "Agent/API workflow",
  other: "Other",
};

const timelineLabels: Record<DemoTimeline, string> = {
  now: "Now",
  this_quarter: "This quarter",
  exploring: "Exploring",
};

const productInterestLabels: Record<ProductInterest, string> = {
  "haulbase-agent": "Haulbase Agent",
  "haulbase-atms": "Haulbase ATMS",
  "headless-haulbase": "Headless Haulbase",
  not_sure: "Not sure yet",
};

export function BookDemoForm() {
  const searchParams = useSearchParams();
  const [state, setState] = useState<SubmitState>({ status: "idle" });

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState({ status: "submitting" });

    const formData = new FormData(event.currentTarget);
    const payload = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("/api/book-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          sourcePath: window.location.pathname,
          sourceSearch: searchParams.toString(),
          referrer: document.referrer,
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        setState({
          status: "error",
          message: result?.fields?.length ? "Please complete the highlighted qualification fields." : "Could not submit the demo request.",
        });
        return;
      }

      setState({
        status: "submitted",
        calendarEligible: Boolean(result.calendarEligible),
        calendarUrl: typeof result.calendarUrl === "string" ? result.calendarUrl : undefined,
      });
      event.currentTarget.reset();
    } catch {
      setState({ status: "error", message: "Could not submit the demo request." });
    }
  }

  if (state.status === "submitted") {
    return (
      <div className="rounded-md border border-[#bddbc8] bg-white p-6 shadow-sm">
        <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#eaf6ef] text-[#267554]">
          <CheckCircle2 size={22} />
        </div>
        <h2 className="mt-5 text-2xl font-semibold tracking-normal text-[#17251f]">Demo request received</h2>
        <p className="mt-3 text-sm leading-6 text-[#596a64]">
          We have enough detail to show the freight problem you want to fix first.
        </p>
        {state.calendarEligible && state.calendarUrl ? (
          <a
            href={state.calendarUrl}
            className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#d95c25] px-4 text-sm font-semibold text-white transition hover:bg-[#bd4817]"
          >
            <CalendarDays size={17} />
            Pick a time
          </a>
        ) : (
          <p className="mt-5 rounded-md border border-[#dbe4df] bg-[#f7faf8] px-4 py-3 text-sm text-[#52645d]">
            The team will review the fit and follow up with the right next step.
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-md border border-[#dbe4df] bg-white p-5 shadow-sm">
      <input className="hidden" name="website" tabIndex={-1} autoComplete="off" />
      <div className="mb-5 rounded-md border border-[#e4ebe7] bg-[#f7faf8] p-4">
        <h2 className="text-lg font-semibold tracking-normal text-[#17251f]">Tell us what is slowing your team down.</h2>
        <p className="mt-2 text-sm leading-6 text-[#596a64]">
          Your answers help us show the right work first: late loads, tracking, PODs, carrier risk, customer updates, or approvals.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" name="name" autoComplete="name" required />
        <Field label="Work email" name="email" type="email" autoComplete="email" required />
        <Field label="Company" name="company" autoComplete="organization" required />
        <Field label="Role" name="role" autoComplete="organization-title" required />
        <SelectField label="Company type" name="companyType" options={companyTypeOptions} labels={companyTypeLabels} required />
        <SelectField label="Monthly load volume" name="monthlyLoadVolume" options={monthlyLoadVolumeOptions} labels={monthlyLoadVolumeLabels} required />
        <SelectField label="Biggest pain" name="biggestPain" options={biggestPainOptions} labels={biggestPainLabels} required />
        <SelectField label="Timeline" name="timeline" options={timelineOptions} labels={timelineLabels} required />
      </div>
      <div className="mt-4 grid gap-4">
        <SelectField label="Product interest" name="productInterest" options={productInterestOptions} labels={productInterestLabels} required />
        <Field label="Current TMS or workflow" name="currentWorkflow" required />
        <label className="grid gap-2 text-sm font-semibold text-[#263b34]">
          Anything we should know?
          <textarea
            name="notes"
            rows={4}
            className="min-h-28 rounded-md border border-[#cfdad4] bg-white px-3 py-2 text-sm font-normal text-[#17251f] outline-none transition placeholder:text-[#8b9994] focus:border-[#d95c25] focus:ring-2 focus:ring-[#f1c2aa]"
            placeholder="Example: tracking gaps, late pickups, missing PODs, carrier vetting, customer update pressure, or where your team loses the most time."
          />
        </label>
      </div>

      {state.status === "error" && <p className="mt-4 text-sm font-medium text-[#b42318]">{state.message}</p>}

      <button
        type="submit"
        disabled={state.status === "submitting"}
        className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#d95c25] px-4 text-sm font-semibold text-white transition hover:bg-[#bd4817] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {state.status === "submitting" ? <Loader2 className="animate-spin" size={17} /> : <ArrowRight size={17} />}
        Book a demo
      </button>
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  autoComplete,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <label className="grid gap-2 text-sm font-semibold text-[#263b34]">
      {label}
      <input
        name={name}
        type={type}
        autoComplete={autoComplete}
        required={required}
        className="h-11 rounded-md border border-[#cfdad4] bg-white px-3 text-sm font-normal text-[#17251f] outline-none transition placeholder:text-[#8b9994] focus:border-[#d95c25] focus:ring-2 focus:ring-[#f1c2aa]"
      />
    </label>
  );
}

function SelectField<const T extends readonly string[]>({
  label,
  name,
  options,
  labels,
  required,
}: {
  label: string;
  name: string;
  options: T;
  labels: Record<T[number], string>;
  required?: boolean;
}) {
  const selectOptions = options as readonly T[number][];

  return (
    <label className="grid gap-2 text-sm font-semibold text-[#263b34]">
      {label}
      <select
        name={name}
        required={required}
        defaultValue=""
        className="h-11 rounded-md border border-[#cfdad4] bg-white px-3 text-sm font-normal text-[#17251f] outline-none transition focus:border-[#d95c25] focus:ring-2 focus:ring-[#f1c2aa]"
      >
        <option value="" disabled>
          Select
        </option>
        {selectOptions.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );
}
